import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EyeOff, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCatalogs, catalogMap } from "@/lib/lookups";
import { COMMISSION_STATUS_ORDER } from "@/lib/conecta";
import { curLabel, money } from "@/lib/finance";
import { Section } from "@/components/common/DetailFields";

const StatusBlock = ({ status, label, data, hidden }) => (
  <div data-testid={`board-status-${status}`} className="c-card p-4 space-y-2">
    <p className="text-sm font-semibold">{label}</p>
    {!data ? <p className="text-xs c-muted">Sin acuerdos</p> : Object.entries(data).map(([cur, t]) => (
      <div key={cur} data-testid={`board-${status}-${cur.toLowerCase()}`} className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-mono c-accent w-16">{curLabel(cur)}</span>
        <span className="c-muted">{t.count} acuerdo(s)</span>
        <span className="font-mono tabular-nums text-sm font-semibold text-right">
          {hidden ? "Oculto" : status === "cobrada" ? money(t.collected) : `${money(t.expected)}${t.collected ? ` · cobrado ${money(t.collected)}` : ""}`}
        </span>
      </div>
    ))}
  </div>
);

export default function ComisionesPage() {
  const cats = useCatalogs();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/commissions/board").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!b) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  const statuses = catalogMap(cats, "commission_statuses");
  const allHidden = b.amounts_hidden && b.hidden_amounts_count === b.items.length && b.items.length > 0;
  return (
    <div data-testid="comisiones-page" className="space-y-4">
      {b.amounts_hidden && b.hidden_amounts_count > 0 && (
        <p data-testid="board-hidden-note" className="text-xs c-text-2 flex items-center gap-1.5"><EyeOff className="w-3.5 h-3.5" /> {b.hidden_amounts_count} acuerdo(s) con montos ocultos.</p>
      )}
      <div className="space-y-2.5">
        {COMMISSION_STATUS_ORDER.map((s) => <StatusBlock key={s} status={s} label={statuses[s] || s} data={b.by_status[s]} hidden={allHidden} />)}
      </div>
      <Section title="Ranking de referidores" testid="board-ranking">
        {b.ranking.length === 0 && <p className="text-sm c-muted">Sin datos todavía.</p>}
        <ol className="divide-y c-line">
          {b.ranking.map((r, i) => (
            <li key={r.contact_id} data-testid="ranking-item" className="py-2.5 flex items-center gap-3">
              <span className="w-6 text-center font-mono text-sm c-accent">{i === 0 ? <Trophy className="w-4 h-4 inline" /> : i + 1}</span>
              <Link to={`/conecta/red/${r.contact_id}`} className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{r.name}</p>
                <p className="text-xs c-muted">{r.opportunities_count} negocio(s) traído(s)</p>
              </Link>
              <div className="text-right text-xs font-mono">
                {b.amounts_hidden && !Object.keys(r.by_currency).length ? <span className="c-muted">—</span> :
                  Object.entries(r.by_currency).map(([cur, v]) => <p key={cur}>{money(v.collected)} {curLabel(cur)}</p>)}
              </div>
            </li>
          ))}
        </ol>
      </Section>
      <Section title={`Acuerdos (${b.items.length})`} testid="board-items">
        <ul className="divide-y c-line">
          {b.items.map((a) => (
            <li key={a.id} data-testid="board-item">
              <Link to={`/conecta/negocios/${a.opportunity_id}`} className="py-2.5 flex justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{a.opportunity_title}</p>
                  <p className="text-xs c-muted">{statuses[a.status]} · {a.payer_name || a.requester_name || "Quién paga sin definir"}</p>
                </div>
                <span className="text-xs font-mono text-right whitespace-nowrap">{a.amounts_hidden ? "Oculto" : `${money(a.pending_amount ?? a.expected_amount, a.currency)}`}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
